import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { formatDate } from "@/lib/format/date";
import { getMySubscription } from "../api";

/**
 * Aviso de cobrança em atraso, **antes** do bloqueio (hub#705).
 *
 * Dentro da tolerância o acesso segue liberado e o status vem como `active` — então, sem este
 * aviso, o corretor só descobre o problema quando é barrado no meio de uma tarefa. O banner
 * transforma "fui bloqueado do nada" em "fui avisado e escolhi ignorar".
 *
 * A data não é enfeite: "algo está errado" assusta sem orientar. O prazo é o que permite decidir.
 */
export function PastDueBanner() {
  const { t } = useTranslation("subscription");

  const { data } = useQuery({
    queryKey: ["my-subscription"],
    queryFn: getMySubscription,
    // Relido a cada foco da aba: o corretor sai para pagar e volta — manter o aviso depois de
    // regularizado é pior que não avisar, porque ensina a ignorar o banner.
    refetchOnWindowFocus: true,
    staleTime: 60_000,
  });

  const pastDueSince = data?.pastDueSince;
  const graceEndsAt = data?.graceEndsAt;
  if (!pastDueSince) return null;

  // Depois da tolerância não é mais aviso — é bloqueio, e quem cuida disso é o gate (402 → modal).
  // Insistir aqui duplicaria a mensagem com um prazo que já venceu.
  if (graceEndsAt && new Date(graceEndsAt).getTime() <= Date.now()) return null;

  return (
    <div
      role="alert"
      className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 border-b border-danger-soft bg-danger-soft/60 px-4 py-1.5 text-[12px] font-medium text-danger-ink"
    >
      <AlertTriangle size={13} className="shrink-0" />
      <span>
        {graceEndsAt
          ? t("pastDue.bannerWithDeadline", { date: formatDate(graceEndsAt) })
          : t("pastDue.banner")}
      </span>
      <Link
        to="/settings/account"
        className="underline underline-offset-2 hover:no-underline"
      >
        {t("pastDue.cta")}
      </Link>
    </div>
  );
}
